import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ApiCallingService } from './api-calling.service';
import { Globals } from '../global';
import { ConfigApi } from '../../config-api';

export const sharedBroadcast = new BroadcastChannel('');

@Injectable({
  providedIn: 'root',
})
export class IdleService {
  private idleInterval: any;
  private inactivityTime = 0;
  private readonly inactivityTimeout = 20 * 60 * 1000; // 20 minutes
  private midnightTimeout: any;
  private isLoggingOut = false;

  toastMessageSignal = signal<any | null>(null);

  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private router: Router,
    private messageService: MessageService,
    private apiCallingService: ApiCallingService,
    private globals: Globals
  ) {
    sharedBroadcast.onmessage = (message) => {
      switch (message.data) {
        case 'logOut':
          this.stopInterval();

          this.toastMessageSignal.set({
            severity: 'success',
            summary: 'Logout',
            detail: 'You have Logged Out Successfully',
          });

          window.location.reload();
          break;

        case 'logOut-noActivity':
          this.stopInterval();

          this.toastMessageSignal.set({
            severity: 'success',
            summary: 'Logout',
            detail:
              'You have been forcefully logged out. Please log in again',
          });

          if (typeof window.caches !== 'undefined') {
            window.caches
              .keys()
              .then((names) =>
                Promise.all(
                  names.map((name) => window.caches.delete(name))
                )
              )
              .finally(() => {
                window.location.reload();
              });
          } else {
            window.location.reload();
          }

          break;

        case 'resetInactivityTimer':
          this.inactivityTime = 0;
          break;

        default:
          console.warn('Unhandled message:', message.data);
      }
    };
  }

  public startInactivityTimer(): void {
    // Prevent multiple inactivity intervals.
    if (this.idleInterval) {
      return;
    }

    this.isLoggingOut = false;

    if (!localStorage.getItem('lastActiveTimestamp')) {
      localStorage.setItem(
        'lastActiveTimestamp',
        Date.now().toString()
      );
    }

    this.checkInactivity();

    this.idleInterval = setInterval(() => {
      this.zone.run(() => {
        this.checkInactivity();
      });
    }, 10 * 1000);

    this.scheduleMidnightLogout();
  }

  private scheduleMidnightLogout(): void {
    console.log(
      'Browser Timezone:',
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );

    const now = new Date();

    const midnight = new Date();
    midnight.setDate(midnight.getDate() + 1);
    midnight.setHours(0, 0, 0, 0);

    const timeUntilMidnight = midnight.getTime() - now.getTime();

    console.log('Current Time:', now);
    console.log('Scheduled Logout At:', midnight);

    this.midnightTimeout = setTimeout(() => {
      this.logout('midnight', 'idle service scheduleMidnightLogout');
    }, timeUntilMidnight);
  }

  private checkInactivity(): void {
    if (this.isLoggingOut) {
      return;
    }

    const lastActive = localStorage.getItem('lastActiveTimestamp');

    if (!lastActive) {
      localStorage.setItem(
        'lastActiveTimestamp',
        Date.now().toString()
      );
      return;
    }

    const lastActiveTime = Number(lastActive);

    if (!Number.isFinite(lastActiveTime)) {
      localStorage.setItem(
        'lastActiveTimestamp',
        Date.now().toString()
      );
      return;
    }

    const timePassed = Date.now() - lastActiveTime;

    if (timePassed >= this.inactivityTimeout) {
      this.logout('noActivity', 'idle service from checkInactivity');
    }
  }

  stopInterval(): void {
    clearInterval(this.idleInterval);
    this.idleInterval = undefined;

    clearTimeout(this.midnightTimeout);
    this.midnightTimeout = undefined;
  }

  public resetInactivityTimer(
    event?: MouseEvent | KeyboardEvent
  ): void {
    if (this.isLoggingOut) {
      return;
    }

    this.inactivityTime = 0;

    localStorage.setItem(
      'lastActiveTimestamp',
      Date.now().toString()
    );

    sharedBroadcast.postMessage('resetInactivityTimer');
  }

  logout(type: string = '', logoutfrom?: string): void {
    console.log('idleservice Logout trigger type', type);
    console.log('idleservice Logout logoutfrom', logoutfrom);
    // Prevent repeated logout API calls.
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;

    this.stopInterval();

    this.apiCallingService
      .callAuthPostAPI({}, 'POST', ConfigApi.URLS.LOGOUT)
      .subscribe({
        next: (response: any) => {
          response = JSON.parse(response);


          if (response.responseCode == 200) {
            localStorage.clear();

            if (type && type === 'noActivity') {
              this.toastMessageSignal.set({
                severity: 'success',
                summary: 'Logout',
                detail:
                  'You have been forcefully logged out. Please log in again',
              });

              sharedBroadcast.postMessage('logOut-noActivity');

              this.router.navigate(['/login']).then(() => {
                localStorage.clear();

                if (typeof window.caches !== 'undefined') {
                  window.caches
                    .keys()
                    .then((names) =>
                      Promise.all(
                        names.map((name) => window.caches.delete(name))
                      )
                    )
                    .finally(() => {
                      window.location.reload();
                    });
                } else {
                  window.location.reload();
                }
              });
            } else {
              this.toastMessageSignal.set({
                severity: 'success',
                summary: 'Logout',
                detail:
                  type && type === 'Unauthorized'
                    ? 'Unauthorized access. Please log in again'
                    : response.responseMessage.charAt(0).toUpperCase() +
                    response.responseMessage.slice(1).toLowerCase(),
              });

              sharedBroadcast.postMessage('logOut');

              this.router.navigate(['/login']);
            }
          } else {
            this.isLoggingOut = false;
          }
        },

        error: (response) => {
          this.isLoggingOut = false;

          let err =
            typeof response === 'string'
              ? JSON.parse(response)
              : response;

          this.toastMessageSignal.set({
            severity: 'error',
            summary: 'Error',
            detail:
              err.responseMessage.charAt(0).toUpperCase() +
              err.responseMessage.slice(1).toLowerCase(),
          });
        },
      });
  }
}
